import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import IllustrationMap from 'shared/components/vxl/Illustration/IllustrationMap';
import { formatCurrency } from 'shared/utils/string/currency';

export const DEFAULT_SWEEPSTAKES_ILLUSTRATION: IllustrationKeyType = 'CASH_F';

export interface SweepstakesEntryTier {
  label: string;
  chances: string;
}

export interface SweepstakesPrize {
  date: string;
  amount_display: string;
  winner_count: number;
  label?: string;
}

export interface SweepstakesContentV1 {
  intro: string;
  entry_tiers_title?: string;
  entry_tiers_description?: string;
  entry_tiers_display: SweepstakesEntryTier[];
  bonus_callout?: string;
  max_prize_display: string;
  prize_schedule_title?: string;
  prize_schedule_description?: string;
  prize_schedule: SweepstakesPrize[];
  illustration: IllustrationKeyType;
}

interface ParsedPrize {
  amount: number;
  prize: SweepstakesPrize;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isString = (value: unknown): value is string => typeof value === 'string';

const optionalString = (value: unknown): string | undefined =>
  isString(value) ? value : undefined;

const isIllustrationKey = (value: unknown): value is IllustrationKeyType =>
  isString(value) && Object.keys(IllustrationMap).includes(value);

const parseAmount = (value: unknown): number | undefined => {
  if (!isString(value) || value.trim().length === 0) {
    return undefined;
  }
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : undefined;
};

const formatPrizeAmount = (amount: number) =>
  formatCurrency(amount, !Number.isInteger(amount));

const parseList = <T>(
  value: unknown,
  parseItem: (item: unknown) => T | undefined,
): T[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const items: T[] = [];
  for (const item of value) {
    const parsedItem = parseItem(item);
    if (!parsedItem) {
      return undefined;
    }
    items.push(parsedItem);
  }

  return items;
};

const parseEntryTier = (value: unknown): SweepstakesEntryTier | undefined =>
  isRecord(value) && isString(value.label) && isString(value.chances)
    ? { label: value.label, chances: value.chances }
    : undefined;

const parsePrize = (value: unknown): ParsedPrize | undefined => {
  if (
    !isRecord(value) ||
    !isString(value.date) ||
    typeof value.winner_count !== 'number'
  ) {
    return undefined;
  }

  const amount = parseAmount(value.amount_display);
  if (amount === undefined) {
    return undefined;
  }

  return {
    amount,
    prize: {
      date: value.date,
      amount_display: formatPrizeAmount(amount),
      winner_count: value.winner_count,
      label: optionalString(value.label),
    },
  };
};

export const parseSweepstakesContentV1 = (
  value: unknown,
): SweepstakesContentV1 | undefined => {
  if (!isRecord(value) || !isString(value.intro)) {
    return undefined;
  }

  const entryTiers = parseList(value.entry_tiers_display, parseEntryTier);
  const prizes = parseList(value.prize_schedule, parsePrize);
  if (!entryTiers || !prizes || prizes.length === 0) {
    return undefined;
  }

  const maxPrizeAmount = Math.max(...prizes.map(({ amount }) => amount));

  return {
    intro: value.intro,
    entry_tiers_title: optionalString(value.entry_tiers_title),
    entry_tiers_description: optionalString(value.entry_tiers_description),
    entry_tiers_display: entryTiers,
    bonus_callout: optionalString(value.bonus_callout),
    max_prize_display: formatPrizeAmount(maxPrizeAmount),
    prize_schedule_title: optionalString(value.prize_schedule_title),
    prize_schedule_description: optionalString(
      value.prize_schedule_description,
    ),
    prize_schedule: prizes.map(({ prize }) => prize),
    illustration: isIllustrationKey(value.illustration)
      ? value.illustration
      : DEFAULT_SWEEPSTAKES_ILLUSTRATION,
  };
};
